// =============================================================================
// io/sync/duplicateScan.ts — the two reads behind the duplicate-trade tool.
// -----------------------------------------------------------------------------
// ── 🚨 TWO READS, NOT ONE, AND THE SPLIT IS THE WHOLE DESIGN ──
//
// The banner has to be correct the moment the Accounts tab opens, which means
// scanning the WHOLE BOOK on every visit. The old client gets that free — it
// holds every trade in memory. Here it is a network read, so it has to be cheap:
//
//   1. `loadDuplicateSignatures` — nine small columns over every trade. Enough
//      to group, and nothing more. This is the one that runs on tab load.
//   2. `loadDuplicateDetail` — the annotation and excursion columns, for the
//      CANDIDATE IDS ONLY. Runs when the trader opens the tool, over a few dozen
//      rows rather than the book.
//
// Doing it in one pass would mean pulling `notes`, `screenshots`, `recordings`,
// `confluences` and the fill timeline for every trade the user owns, every time
// they look at their accounts — to draw a banner that is usually absent.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { PAGE_ROWS } from './load';
import type { DupRichTrade, DupSigTrade } from '../../domain/duplicateTrades';

const SIG_SELECT = [
  'id', 'account_id', 'date', 'time_in', 'contract', 'side', 'qty', 'entry', 'exit',
].join(',');

/**
 * ⚠ `executions` RIDES `extras`, read by sub-path like every other consumer.
 * Reading the whole `extras` blob would pull the excursion walk and every other
 * annexed field with it, on rows this only needs a length from.
 */
const DETAIL_SELECT = [
  'id', 'account_id', 'date', 'time_in', 'contract', 'side', 'qty', 'entry', 'exit',
  'net_pnl', 'notes', 'pattern_id', 'exit_model_id', 'grade', 'session_label',
  'market_regime', 'news_event', 'mistake_tags', 'confluences', 'booleans',
  'screenshots', 'recordings', 'mae_points', 'mfe_points', 'mae_secs', 'mfe_secs',
  'executions:extras->executions',
].join(',');

const str = (v: unknown): string | null =>
  (v === null || v === undefined ? null : String(v));
const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const arr = (v: unknown): readonly unknown[] | null =>
  (Array.isArray(v) ? v : null);

function sigFromDb(r: Record<string, unknown>): DupSigTrade {
  return {
    id: String(r['id'] ?? ''),
    accountId: str(r['account_id']),
    date: str(r['date']),
    timeIn: str(r['time_in']),
    contract: str(r['contract']),
    side: str(r['side']),
    qty: numOrNull(r['qty']),
    entry: numOrNull(r['entry']),
    exit: numOrNull(r['exit']),
  };
}

/** A candidate row, with everything the "which copy survives" ranking reads. */
export interface DupDetailTrade extends DupRichTrade {
  netPnl: number | null;
}

function detailFromDb(r: Record<string, unknown>): DupDetailTrade {
  return {
    ...sigFromDb(r),
    netPnl: numOrNull(r['net_pnl']),
    notes: str(r['notes']),
    patternId: str(r['pattern_id']),
    exitModelId: str(r['exit_model_id']),
    grade: str(r['grade']),
    sessionLabel: str(r['session_label']),
    marketRegime: str(r['market_regime']),
    newsEvent: str(r['news_event']),
    mistakeTags: (arr(r['mistake_tags']) as readonly string[] | null),
    confluences: (r['confluences'] ?? null) as Record<string, unknown> | null,
    booleans: (r['booleans'] ?? null) as Record<string, unknown> | null,
    screenshots: arr(r['screenshots']),
    recordings: arr(r['recordings']),
    maePoints: numOrNull(r['mae_points']),
    mfePoints: numOrNull(r['mfe_points']),
    maeSecs: numOrNull(r['mae_secs']),
    mfeSecs: numOrNull(r['mfe_secs']),
    executions: arr(r['executions']),
  };
}

/**
 * Every trade's signature fields.
 *
 * ⚠ PAGES TO EXHAUSTION AND THROWS ON A BAD PAGE. A short read here does not
 * look short — it looks like FEWER DUPLICATES, and the banner would quietly
 * under-report. Silence on this screen means "your book is clean", so a partial
 * answer is worse than an error.
 */
export async function loadDuplicateSignatures(
  sb: SupabaseClient,
): Promise<DupSigTrade[]> {
  const out: DupSigTrade[] = [];
  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('trades').select(SIG_SELECT)
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);
    if (error) {
      throw new Error(
        `Could not scan your trades for duplicates (page ${(from / PAGE_ROWS) + 1}): `
        + `${error.message}`,
      );
    }
    if (!data || !data.length) break;
    for (const r of data as unknown as Record<string, unknown>[]) out.push(sigFromDb(r));
    if (data.length < PAGE_ROWS) break;
  }
  return out;
}

/**
 * The full rows for the candidates the scan found.
 *
 * ⚠ CHUNKED BY ID, and it throws on any failed chunk for the same reason: a
 * missing chunk would silently shrink a group, and a group of two that comes
 * back as one is a group the tool would then propose deleting nothing from —
 * or, worse, would rank on incomplete richness and keep the poorer copy.
 */
export async function loadDuplicateDetail(
  sb: SupabaseClient, ids: readonly string[],
): Promise<DupDetailTrade[]> {
  const out: DupDetailTrade[] = [];
  for (let i = 0; i < ids.length; i += PAGE_ROWS) {
    const chunk = ids.slice(i, i + PAGE_ROWS);
    const { data, error } = await sb
      .from('trades').select(DETAIL_SELECT)
      .in('id', chunk)
      .order('id', { ascending: true });
    if (error) {
      throw new Error(`Could not read the duplicate trades: ${error.message}`);
    }
    for (const r of (data ?? []) as unknown as Record<string, unknown>[]) {
      out.push(detailFromDb(r));
    }
  }
  return out;
}
